import type { BaseTableParams } from "@dashboardApi/apis/types";
import type { BaseTableResultItem, TableResult } from "@tanstack/react-table";

import type { FetchRequestError } from "@lib/errors";
import { FetchUserEncodedError } from "@lib/errors/FetchUserEncodedError";
import type { Queries } from "@lib/serializeQueryObjects";
import type { SHARED_CONSTANTS_DECISIONING_STUDIO_ERROR_CODES } from "@lib/shared_constants";

import { isPostDecisioningStudioAgentsAppGroupIdNameTakenMeta } from "./validators";

export interface DecisioningStudioAgentsAppGroupIdParams extends BaseTableParams {
  /** the query object containing all the filters */
  query?: Queries;
}

export interface GetDecisioningStudioAgentsAppGroupIdResultItem extends BaseTableResultItem {
  id: string;
  of_client_slug: string;
  of_pipeline_slug: string;
  of_usecase_slug: string;
}

export type GetDecisioningStudioAgentsAppGroupIdResult =
  TableResult<GetDecisioningStudioAgentsAppGroupIdResultItem>;

export interface PostDecisioningStudioAgentsAppGroupIdProps {
  /** The unique identifier of the app group */
  appGroupId: string;
  /** Agent name; the backend computes all slugs from it — the caller never sends slugs. */
  displayName: string;
}

export interface PostDecisioningStudioAgentsAppGroupIdResult {
  id: string;
  of_client_slug: string;
  of_pipeline_slug: string;
  of_usecase_slug: string;
  frequency_cap: boolean;
  exclude_from_global_frequency_count: boolean;
}

export interface PostDecisioningStudioAgentsAppGroupIdNameTakenMeta {
  /** Display name of the agent already holding the computed slug; null when it predates display names. */
  display_name: string | null;
}

/** Usecase-slug collision rejection: the requested name maps to a slug another agent already holds. */
export type PostDecisioningStudioAgentsAppGroupIdNameTakenError = FetchUserEncodedError<
  SHARED_CONSTANTS_DECISIONING_STUDIO_ERROR_CODES["AGENT_NAME_TAKEN"],
  PostDecisioningStudioAgentsAppGroupIdNameTakenMeta
>;

/**
 * Narrows a create failure to the name-collision rejection.
 *
 * Keyed off `error_code`, never HTTP status: the controller returns 422 while the Storybook
 * fakes may carry other statuses with the same body. A malformed meta returns false and the
 * caller falls back to the generic form-level copy.
 */
export const isPostDecisioningStudioAgentsAppGroupIdNameTakenError = (
  error: FetchRequestError
): error is PostDecisioningStudioAgentsAppGroupIdNameTakenError => {
  if (!(error instanceof FetchUserEncodedError) || error.errorCode !== "AGENT_NAME_TAKEN") {
    return false;
  }

  return isPostDecisioningStudioAgentsAppGroupIdNameTakenMeta(error.meta);
};
